// @ts-nocheck
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { Id } from '@/convex/_generated/dataModel';
import { useAuth } from '@/hooks/useAuth';
import { motion, AnimatePresence } from 'framer-motion';
import { useEffect } from 'react';

type ArchivedUrl = {
	_id: Id<'urls'>;
	url: string;
	username?: string;
	podId?: Id<'engagementPods'> | null;
	archivedAt?: string;
	platform: string;
	mode?: string;
	profileKey?: string | null;
};

export function UrlHistory() {
	const { toast } = useToast();
	const { user, isAdmin } = useAuth();
	const location = useLocation();
	const navigate = useNavigate();
	const [activeTab, setActiveTab] = React.useState('twitter');
	const [searchTerm, setSearchTerm] = React.useState('');
	const [expandedGroups, setExpandedGroups] = React.useState<Set<string>>(
		new Set()
	);
	const [expandedModes, setExpandedModes] = React.useState<Set<string>>(
		new Set()
	);

	// Skip queries if user is not available
	const archivedUrls = useQuery(
		api.urls.listArchived,
		!user?.id ? 'skip' : { podId: undefined }
	);

	const pods = useQuery(
		api.engagementPods.list,
		!user?.id
			? 'skip'
			: {
					userId: null,
					requestingUserId: user.id as unknown as string,
				}
	);

	const profiles = useQuery(
		api.profiles.list,
		!user?.id
			? 'skip'
			: {
					userId: user.id as unknown as Id<'users'>,
					isSuperAdmin: Boolean(isAdmin),
				}
	);

	const reactivateUrl = useMutation(api.urls.reactivateUrl);

	const fromPath = location.state?.from || '/admin/urls';

	useEffect(() => {
		const handlePopState = () => {
			if (location.pathname === '/admin/urls/history') {
				navigate(fromPath, { replace: true });
			}
		};

		window.addEventListener('popstate', handlePopState);
		return () => {
			window.removeEventListener('popstate', handlePopState);
		};
	}, [navigate, fromPath, location.pathname]);

	const handleReactivateUrl = async (urlId: Id<'urls'>) => {
		try {
			await reactivateUrl({ urlId });
			toast({
				title: '✨ URL Reactivated ✨',
				description: 'URL has been successfully reactivated.',
				duration: 3000,
			});
		} catch (error) {
			toast({
				title: 'Error',
				description:
					error instanceof Error
						? error.message
						: 'Failed to reactivate URL',
				variant: 'destructive',
			});
		}
	};

	const filterUrls = (urls: ArchivedUrl[]) => {
		return urls.filter((url) => {
			const searchLower = searchTerm.toLowerCase();
			const podName =
				pods
					?.find((p: any) => p._id === url.podId)
					?.name?.toLowerCase() || '';
			const profileName =
				profiles
					?.find((p: any) => p.profileKey === url.profileKey)
					?.profileTitle?.toLowerCase() || '';
			const podCreator = pods?.find(
				(p: any) => p._id === url.podId
			)?.userId;
			const podCreatorName = podCreator
				? pods
						?.find((p: any) => p._id === url.podId)
						?.name?.toLowerCase()
				: '';

			return (
				url.url.toLowerCase().includes(searchLower) ||
				url.username?.toLowerCase().includes(searchLower) ||
				podName.includes(searchLower) ||
				profileName.includes(searchLower) ||
				podCreatorName?.includes(searchLower)
			);
		});
	};

	const renderArchivedUrls = (platform: string) => {
		const filteredUrls = filterUrls(
			archivedUrls?.filter(
				(url: any) => url.platform === platform.toLowerCase()
			) || []
		);

		const sortedUrls = [...filteredUrls].sort((a, b) => {
			return (
				new Date(b.archivedAt || '').getTime() -
				new Date(a.archivedAt || '').getTime()
			);
		});

		const urlsByMode = sortedUrls.reduce(
			(acc, url) => {
				const mode = url.mode === 'broadcast' ? 'broadcast' : 'swarm';
				if (!acc[mode]) {
					acc[mode] = [];
				}
				acc[mode].push(url);
				return acc;
			},
			{} as Record<string, ArchivedUrl[]>
		);

		return (
			<div className='space-y-8 mt-6'>
				{Object.entries(urlsByMode).map(([mode, modeUrls]) => (
					<Card key={mode} className='shadow-md'>
						<CardHeader>
							<div className='flex items-center gap-2'>
								<motion.button
									onClick={() =>
										setExpandedModes((prev) => {
											const next = new Set(prev);
											if (next.has(mode)) {
												next.delete(mode);
											} else {
												next.add(mode);
											}
											return next;
										})
									}
									className='w-8 h-8 rounded-full bg-purple-900 flex items-center justify-center text-white hover:bg-purple-700 transition-colors'
									animate={{
										rotate: expandedModes.has(mode)
											? 180
											: 0,
									}}
									transition={{
										duration: 0.2,
										ease: 'easeInOut',
									}}
								>
									{expandedModes.has(mode) ? '🔽' : '▶️'}
								</motion.button>
								<CardTitle
									className={cn(
										'text-2xl font-bold uppercase',
										mode === 'broadcast'
											? 'text-orange-500'
											: 'text-pink-500'
									)}
								>
									{mode} Mode
								</CardTitle>
							</div>
						</CardHeader>
						<AnimatePresence>
							{expandedModes.has(mode) && (
								<motion.div
									initial={{ opacity: 0, height: 0 }}
									animate={{ opacity: 1, height: 'auto' }}
									exit={{ opacity: 0, height: 0 }}
									transition={{ duration: 0.2 }}
								>
									<CardContent className='space-y-6'>
										{Object.entries(
											modeUrls.reduce(
												(acc, url) => {
													const podId =
														url.podId ??
														'unassigned';
													if (!acc[podId]) {
														acc[podId] = [];
													}
													acc[podId].push(url);
													return acc;
												},
												{} as Record<
													| string
													| Id<'engagementPods'>,
													typeof modeUrls
												>
											)
										).map(([podId, urls]) => {
											const pod = pods?.find(
												(p: any) => p._id === podId
											);
											const displayName =
												mode === 'broadcast'
													? profiles?.find(
															(p: any) =>
																p.profileKey ===
																urls[0]
																	.profileKey
														)?.profileTitle
													: pod?.name;

											return (
												<Card key={podId}>
													<CardHeader>
														<div className='flex items-center gap-2'>
															<motion.button
																onClick={() =>
																	setExpandedGroups(
																		(
																			prev
																		) => {
																			const next =
																				new Set(
																					prev
																				);
																			if (
																				next.has(
																					podId
																				)
																			) {
																				next.delete(
																					podId
																				);
																			} else {
																				next.add(
																					podId
																				);
																			}
																			return next;
																		}
																	)
																}
																className='w-8 h-8 rounded-full bg-purple-900 flex items-center justify-center text-white hover:bg-purple-700 transition-colors'
																animate={{
																	rotate: expandedGroups.has(
																		podId
																	)
																		? 180
																		: 0,
																}}
																transition={{
																	duration: 0.2,
																	ease: 'easeInOut',
																}}
															>
																{expandedGroups.has(
																	podId
																)
																	? '🔽'
																	: '▶️'}
															</motion.button>
															<CardTitle className='flex items-center gap-2'>
																<span className='text-gray-500'>
																	{mode ===
																	'broadcast'
																		? 'Profile:'
																		: 'Pod Name:'}
																</span>
																<span className='font-bold'>
																	{displayName ||
																		(mode ===
																		'broadcast'
																			? 'Unknown Profile'
																			: 'Unknown Pod')}
																</span>
															</CardTitle>
														</div>
													</CardHeader>
													<AnimatePresence>
														{expandedGroups.has(
															podId
														) && (
															<motion.div
																initial={{
																	opacity: 0,
																	height: 0,
																}}
																animate={{
																	opacity: 1,
																	height: 'auto',
																}}
																exit={{
																	opacity: 0,
																	height: 0,
																}}
																transition={{
																	duration: 0.2,
																}}
															>
																<CardContent className='space-y-4'>
																	{urls.map(
																		(
																			url
																		) => (
																			<Card
																				key={
																					url._id
																				}
																				className='p-4'
																			>
																				<div className='flex justify-between items-start'>
																					<div className='space-y-2'>
																						<div className='flex items-center gap-2'>
																							<h3 className='font-semibold'>
																								{url.username ||
																									'Unknown User'}
																							</h3>
																							<span className='px-2 py-1 rounded-full text-xs font-semibold bg-gray-200 text-gray-700'>
																								ARCHIVED
																							</span>
																						</div>
																						<p className='text-sm text-gray-500'>
																							<a
																								href={
																									url.url
																								}
																								target='_blank'
																								rel='noopener noreferrer'
																								className='text-blue-500 hover:text-blue-700'
																							>
																								{
																									url.url
																								}
																							</a>
																						</p>
																						<p className='text-xs text-gray-400'>
																							Archived:{' '}
																							{new Date(
																								url.archivedAt ||
																									''
																							).toLocaleString()}
																						</p>
																					</div>
																					<Button
																						variant='outline'
																						size='sm'
																						onClick={() =>
																							handleReactivateUrl(
																								url._id
																							)
																						}
																						className='bg-emerald-600 hover:bg-emerald-700 text-white'
																					>
																						↻
																						Reactivate
																					</Button>
																				</div>
																			</Card>
																		)
																	)}
																</CardContent>
															</motion.div>
														)}
													</AnimatePresence>
												</Card>
											);
										})}
									</CardContent>
								</motion.div>
							)}
						</AnimatePresence>
					</Card>
				))}
			</div>
		);
	};

	return (
		<div className='container mx-auto p-6'>
			<div className='flex justify-between items-center mb-6'>
				<h1 className='text-3xl font-bold'>URL History</h1>
				<Button variant='outline' asChild>
					<Link
						to={
							fromPath.startsWith('/admin/pods/')
								? '/admin/pods'
								: fromPath
						}
						className='flex items-center gap-2'
						onClick={(e) => {
							e.preventDefault();
							navigate(
								fromPath.startsWith('/admin/pods/')
									? '/admin/pods'
									: fromPath
							);
						}}
					>
						← Back
					</Link>
				</Button>
			</div>

			<div className='mb-6'>
				<Input
					placeholder='Search URLs, usernames, or pod names...'
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
					className='max-w-md'
				/>
			</div>

			<Tabs
				defaultValue='twitter'
				className='space-y-4'
				onValueChange={setActiveTab}
			>
				<TabsList>
					<TabsTrigger value='twitter'>Twitter X</TabsTrigger>
					<TabsTrigger value='facebook'>Facebook</TabsTrigger>
				</TabsList>

				<TabsContent value='twitter'>
					{renderArchivedUrls('twitter')}
				</TabsContent>

				<TabsContent value='facebook'>
					{renderArchivedUrls('facebook')}
				</TabsContent>
			</Tabs>
		</div>
	);
}
